import React, { useEffect, useState } from "react";
import { inr } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

let _rainCfg = null;
export const useRainSettings = () => {
  const [cfg, setCfg] = useState(_rainCfg);
  useEffect(() => {
    if (_rainCfg) return;
    api.get("/settings").then(({ data }) => {
      _rainCfg = { enabled: data.rain_enabled !== false, rate: Number(data.rain_amount ?? 20), max: Number(data.rain_max_orders || 100) };
      setCfg(_rainCfg);
    }).catch(() => {});
  }, []);
  return cfg || { enabled: true, rate: 20, max: 100 };
};
export const resetRainSettings = () => { _rainCfg = null; };

export const rainInvalid = (f) => Number(f?.rain_orders || 0) > Number(f?.orders || 0) || Number(f?.rain_orders || 0) < 0;

export const RainField = ({ form, set, prefix, preview }) => {
  const cfg = useRainSettings();
  const total = Number(form.orders || 0);
  const ro = Number(form.rain_orders || 0);
  const rate = preview?.rain_rate_applied ?? (cfg.enabled ? cfg.rate : 0);
  const enabled = preview ? preview.rain_enabled_applied !== false : cfg.enabled;
  const over = ro > total;
  return (
    <div data-testid={`${prefix}-rain-field`}>
      <label className="text-sm font-medium">Number of Rain Orders</label>
      <select data-testid={`${prefix}-rain-orders-select`} value={form.rain_orders ?? "0"} onChange={(e) => set("rain_orders", e.target.value)}
        className={`mt-1 flex h-10 w-full rounded-md border bg-background px-3 text-sm ${over ? "border-rose-500" : "border-input"}`}>
        {Array.from({ length: Math.max(cfg.max, ro) + 1 }, (_, i) => <option key={i} value={String(i)}>{i}</option>)}
      </select>
      <p className="mt-1 text-[11px] leading-tight text-muted-foreground" data-testid={`${prefix}-rain-hint`}>
        {over ? <span className="font-medium text-rose-500">Rain orders cannot exceed total orders.</span>
          : enabled ? <>Rain Surcharge: ₹{rate} per order · Total Rain Earnings: <b className="text-foreground">{inr(preview?.rain_charges ?? ro * rate)}</b></>
            : "Rain surcharge is turned off"}
      </p>
    </div>
  );
};

export const RainRows = ({ p }) => (p.rain_legacy_flat > 0
  ? <Row l="Rain Charges (old flat)" v={inr(p.rain_charges)} testId="breakdown-rain-legacy" />
  : <>
    <Row l="Rain Orders" v={p.rain_orders || 0} testId="breakdown-rain-orders" />
    <Row l="Rain Surcharge Per Order" v={inr(p.rain_rate_applied || 0)} testId="breakdown-rain-rate" />
    <Row l="Total Rain Charges" v={inr(p.rain_charges || 0)} testId="breakdown-rain-total" />
  </>);

export const OrderInputs = ({ form, set, prefix, preview }) => {
  const { can } = useAuth();
  const total = Number(form.orders || 0);
  const multi = Number(form.multi_orders || 0);
  const invalid = multi > total || multi < 0;
  return (
    <>
      <div>
        <label className="text-sm font-medium">Total Orders</label>
        <input data-testid={`${prefix}-orders-input`} type="number" min="0" autoFocus value={form.orders}
          onChange={(e) => set("orders", e.target.value)} placeholder="e.g. 48"
          className="mt-1 flex h-10 w-full rounded-md border border-input bg-transparent px-3 text-lg font-semibold" />
      </div>
      <div>
        <label className="text-sm font-medium">Multi Orders</label>
        <input data-testid={`${prefix}-multi-orders-input`} type="number" min="0" value={form.multi_orders}
          onChange={(e) => set("multi_orders", e.target.value)} placeholder="0"
          className={`mt-1 flex h-10 w-full rounded-md border bg-transparent px-3 text-lg font-semibold ${invalid ? "border-rose-500" : "border-input"}`} />
      </div>
      <p className="col-span-2 -mt-1 text-xs" data-testid={`${prefix}-normal-orders`}>
        {invalid
          ? <span className="font-medium text-rose-500">Multi orders cannot exceed total orders.</span>
          : <span className="text-muted-foreground">Normal Orders: <b className="tabular text-foreground">{total - multi}</b> · Multi Orders: <b className="tabular text-foreground">{multi}</b></span>}
      </p>
      {can("daily.penalty") && [["denial_orders", "Number of Denial Orders"], ["cancelled_orders", "Number of Cancelled Orders"]].map(([k, l]) => (
        <div key={k}>
          <label className="text-sm font-medium">{l}</label>
          <select data-testid={`${prefix}-${k.replace("_", "-")}-select`} value={form[k] ?? "0"} onChange={(e) => set(k, e.target.value)}
            className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
            {Array.from({ length: 101 }, (_, i) => <option key={i} value={String(i)}>{i}</option>)}
          </select>
        </div>
      ))}
      <RainField form={form} set={set} prefix={prefix} preview={preview} />
    </>
  );
};

export const PenaltyRows = ({ p }) => (
  <>
    <Row l={`Denial Orders (${p.denial_orders || 0} × ₹${p.denial_rate_applied ?? 0})`} v={`− ${inr(p.denial_penalty || 0)}`} testId="breakdown-denial" />
    <Row l={`Cancelled Orders (${p.cancelled_orders || 0} × ₹${p.cancel_rate_applied ?? 0})`} v={`− ${inr(p.cancel_penalty || 0)}`} testId="breakdown-cancel" />
    <Row l="Total Penalties" v={`− ${inr(p.total_penalties || 0)}`} testId="breakdown-penalties" />
    <Row l="Gross (before penalties)" v={inr(p.gross_earnings ?? p.total_earnings)} testId="breakdown-gross" />
  </>
);

export const multiInvalid = (f) => Number(f?.multi_orders || 0) > Number(f?.orders || 0) || Number(f?.multi_orders || 0) < 0;

export const BaseRows = ({ p }) => {
  if (p.multi_order_applied && p.multi_orders > 0) {
    return (
      <>
        <Row l={`Normal Orders (${p.normal_orders} × ₹${p.rate})`} v={inr(p.normal_earnings)} testId="breakdown-normal" />
        <Row l={`Multi Orders (${p.multi_orders} × ₹${p.applied_multi_rate})`} v={inr(p.multi_earnings)} testId="breakdown-multi" />
      </>
    );
  }
  return (
    <>
      <Row l={`Base (${p.order_count} × ₹${p.rate})`} v={inr(p.base_earnings)} testId="breakdown-base" />
      {p.multi_orders > 0 && <p className="text-[11px] text-muted-foreground">Multi orders are paid at the normal rate (multi-order rate not enabled).</p>}
    </>
  );
};

export const Row = ({ l, v, testId }) => (
  <div className="flex items-center justify-between" data-testid={testId}><span className="text-muted-foreground">{l}</span><span className="tabular font-medium">{v}</span></div>
);
